import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { dashboardAPI } from '../../../services/dashboardService';

/**
 * OverviewTab - Pure Tailwind implementation
 */
const OverviewTab = ({ filters, onFilterChange, onViewDetailed }) => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadOverviewStats();
    }, [filters]);

    const loadOverviewStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await dashboardAPI.getOverviewStats(filters);
            setStats(response.data);
        } catch (err) {
            console.error('Failed to load overview stats:', err);
            setError(err.message || 'Failed to load statistics');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="w-full">
                <div className="panel-container" style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="loading-skeleton" style={{ width: '40px', height: '40px' }}></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <p>{error}</p>
                <button onClick={loadOverviewStats} className="retry-button">
                    Retry
                </button>
            </div>
        );
    }

    const statCards = [
        { label: 'Total Users', value: stats?.totalUsers || 0, icon: '👥', color: '#3b82f6' },
        { label: 'Active Users', value: stats?.activeUsers || 0, icon: '✅', color: '#10b981' },
        { label: 'Total Posts', value: stats?.totalPosts || 0, icon: '📝', color: '#a855f7' },
        { label: 'Total Pets', value: stats?.totalPets || 0, icon: '🐾', color: '#f97316' },
        { label: 'Appointments', value: stats?.totalAppointments || 0, icon: '📅', color: '#6366f1' },
        { label: 'Active Clinics', value: stats?.activeClinics || 0, icon: '🏥', color: '#14b8a6' },
        { label: 'Marketplace Orders', value: stats?.marketplaceOrders || 0, icon: '🛒', color: '#ec4899' },
        { label: 'Adoption Posts', value: stats?.adoptionPosts || 0, icon: '❤️', color: '#ef4444' }
    ];

    return (
        <div className="w-full">
            <div className="flex justify-end mb-6">
                <button
                    onClick={onViewDetailed}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors font-medium text-sm"
                >
                    View Detailed Analytics <ArrowRight size={16} />
                </button>
            </div>

            {/* Stats Grid */}
            <div className="stats-grid">
                {statCards.map((stat, idx) => (
                    <div key={idx} className="stat-card group">
                        <div className="stat-header">
                            <div className="flex flex-col">
                                <span className="stat-label">
                                    {stat.label}
                                </span>
                                <span className="stat-value">
                                    {stat.value.toLocaleString()}
                                </span>
                            </div>
                            <div
                                className="stat-icon-wrapper"
                                style={{ backgroundColor: `${stat.color}15`, color: stat.color }}
                            >
                                {stat.icon}
                            </div>
                        </div>
                        {/* Decorative bottom bar */}
                        <div className="absolute bottom-0 left-0 h-1 w-full bg-gray-50">
                            <div
                                className="h-full rounded-r-full opacity-50 transition-all duration-1000"
                                style={{ width: '45%', backgroundColor: stat.color }}
                            ></div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Trends Section */}
            {stats?.trends && (
                <div className="panel-container">
                    <div className="panel-title">
                        <span>📈</span>
                        <h3>Performance Trends</h3>
                    </div>

                    <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
                        {/* User Growth */}
                        <div className="stat-card" style={{ background: 'linear-gradient(to bottom right, #f0fdf4, #ecfdf5)' }}>
                            <div className="relative z-10">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-2 bg-green-100 text-green-600 rounded-lg">
                                        👥
                                    </div>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#15803d', backgroundColor: '#dcfce7', padding: '0.25rem 0.5rem', borderRadius: '9999px', textTransform: 'uppercase' }}>
                                        Growth
                                    </span>
                                </div>
                                <h4 className="stat-label">User Growth</h4>
                                <div className="flex items-baseline gap-md">
                                    <span className="stat-value">
                                        +{stats.trends.userGrowth}%
                                    </span>
                                    <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#16a34a' }}>vs last month</span>
                                </div>
                            </div>
                        </div>

                        {/* Post Engagement */}
                        <div className="stat-card" style={{ background: 'linear-gradient(to bottom right, #eff6ff, #eef2ff)' }}>
                            <div className="relative z-10">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                                        💬
                                    </div>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1d4ed8', backgroundColor: '#dbeafe', padding: '0.25rem 0.5rem', borderRadius: '9999px', textTransform: 'uppercase' }}>
                                        Engagement
                                    </span>
                                </div>
                                <h4 className="stat-label">Post Engagement</h4>
                                <div className="flex items-baseline gap-md">
                                    <span className="stat-value">
                                        +{stats.trends.postEngagement}%
                                    </span>
                                    <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#2563eb' }}>vs last month</span>
                                </div>
                            </div>
                        </div>

                        {/* Appointment Rate */}
                        <div className="stat-card" style={{ background: 'linear-gradient(to bottom right, #faf5ff, #fdf4ff)' }}>
                            <div className="relative z-10">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-2 bg-purple-100 text-purple-600 rounded-lg">
                                        📅
                                    </div>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#7e22ce', backgroundColor: '#f3e8ff', padding: '0.25rem 0.5rem', borderRadius: '9999px', textTransform: 'uppercase' }}>
                                        Conversion
                                    </span>
                                </div>
                                <h4 className="stat-label">Appointment Rate</h4>
                                <div className="flex items-baseline gap-md">
                                    <span className="stat-value">
                                        +{stats.trends.appointmentRate}%
                                    </span>
                                    <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#9333ea' }}>vs last month</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OverviewTab;
